import React, { createContext, useContext, useState, useEffect } from 'react';
import { checkSupabaseConnection, waitForConnection } from '../lib/supabase';
import ConnectionError from '../components/ConnectionError';

interface ConnectionContextType {
  isConnected: boolean;
  checkConnection: () => Promise<boolean>;
  retryConnection: () => Promise<boolean>;
}

const ConnectionContext = createContext<ConnectionContextType | undefined>(undefined);

export function ConnectionProvider({ children }: { children: React.ReactNode }) {
  const [isConnected, setIsConnected] = useState(true);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const checkConnection = async () => {
    try {
      const connected = await checkSupabaseConnection();
      setIsConnected(connected);
      return connected;
    } catch (error) {
      console.error('Connection check failed:', error);
      setIsConnected(false);
      return false;
    }
  };

  const retryConnection = async () => {
    setIsRetrying(true);
    try {
      const connected = await waitForConnection(3, 2000);
      setIsConnected(connected);
      return connected;
    } catch (error) {
      console.error('Connection retry failed:', error);
      return false;
    } finally {
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    const initialCheck = async () => {
      await checkConnection();
      setInitialCheckDone(true);
    };

    initialCheck();

    // Set up periodic connection checks
    const intervalId = setInterval(checkConnection, 30000); // Check every 30 seconds

    return () => clearInterval(intervalId);
  }, []);

  if (!initialCheckDone) {
    return null; // Show nothing during initial load
  }

  if (!isConnected) {
    return <ConnectionError onRetry={retryConnection} isRetrying={isRetrying} />;
  }

  return (
    <ConnectionContext.Provider value={{ isConnected, checkConnection, retryConnection }}>
      {children}
    </ConnectionContext.Provider>
  );
}

export function useConnection() {
  const context = useContext(ConnectionContext);
  if (context === undefined) {
    throw new Error('useConnection must be used within a ConnectionProvider');
  }
  return context;
}